import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TooltipContent,
  TooltipTrigger,
  UserAvatar,
  Switch,
  Label
} from "@/components"
import { Bell, MessageCircle, FileText, PartyPopper, SquareCheckBig, PencilLine, ArrowUpRight } from "lucide-react"
import { formatDistanceToNowStrict } from "date-fns"
import { useFetchNotifications } from "@/hooks/queries/useFetchNotifications"
import { useState } from "react"
import { NotificationType, NotificationWithAvatar } from "@/models/Notification"
import { Tooltip, TooltipProvider } from "@radix-ui/react-tooltip"
import { useMarkNotificationAsRead } from "@/hooks/mutations/useMarkNotificationAsRead.tsx"
import { useTranslation } from "react-i18next"
import { useSettings } from "@/services/store/useSettings.ts"
import NotificationEmptyDark from "@/assets/images/NotificationEmptyDark.png"
import NotificationEmptyLight from "@/assets/images/NotificationEmptyLight.png"

const iconMap: Record<NotificationType, { icon: JSX.Element; bgColor: string }> = {
  [NotificationType.REVIEW_REQUESTED]: { icon: <FileText className="size-4" />, bgColor: "bg-apricot" },
  [NotificationType.PR_SUBMITTED]: { icon: <PartyPopper className="size-4" />, bgColor: "bg-lime" },
  [NotificationType.COMMENT_RESOLVED]: { icon: <SquareCheckBig className="size-4" />, bgColor: "bg-secondary-teal" },
  [NotificationType.PR_UPDATED]: { icon: <PencilLine className="size-4" />, bgColor: "bg-honey" },
  [NotificationType.COMMENT]: { icon: <MessageCircle className="size-4" />, bgColor: "bg-violet" }
}

const Notifications = () => {
  const { t } = useTranslation("Notifications")
  const [unreadOnly, setUnreadOnly] = useState(false)

  const { data: notifications, isLoading } = useFetchNotifications()
  const { markNotificationAsReadMutation } = useMarkNotificationAsRead()

  if (isLoading) {
    return null
  }

  const hasNotifications = notifications && notifications.length > 0

  const filteredNotifications = unreadOnly ? (notifications || []).filter((n) => !n.read) : notifications || []

  const getNotificationIcon = (type: NotificationType) => {
    const entry = iconMap[type]
    if (!entry) return null

    return (
      <div className={`flex size-7 min-h-7 min-w-7 items-center justify-center rounded ${entry.bgColor}`}>
        {entry.icon}
      </div>
    )
  }

  const getNotificationTitle = (type: NotificationType): string => {
    const translationKeys = {
      [NotificationType.REVIEW_REQUESTED]: t("ReviewRequested"),
      [NotificationType.PR_SUBMITTED]: t("PRApproved"),
      [NotificationType.COMMENT_RESOLVED]: t("CommentResolved"),
      [NotificationType.PR_UPDATED]: t("NewCommit"),
      [NotificationType.COMMENT]: t("NewComment")
    }
    return translationKeys[type]
  }

  const formatTimeAgo = (dateString: string) => {
    return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true })
  }

  const handleNotificationClick = async (notification: NotificationWithAvatar) => {
    if (!notification.read) {
      await markNotificationAsReadMutation({ notificationId: notification.id.toString() })
    }
    window.location.href = notification.payload.linkHref
  }

  const NotificationsEmptyState = () => {
    const { selectedTheme } = useSettings()
    return (
      <div className="flex flex-col items-center justify-center p-16 text-center">
        <img
          src={selectedTheme === "dark" ? NotificationEmptyDark : NotificationEmptyLight}
          className="w-[66px]"
          alt="Empty notifications"
        />
        <p className="pt-6 text-[12px] text-hint">{t("NoNotifications")}</p>
      </div>
    )
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <div className="group relative flex size-7 cursor-pointer items-center justify-center rounded bg-border hover:bg-base-dark-border data-[state=open]:bg-base-dark-border">
          <Bell className="size-4" />
          {notifications.some((n) => !n.read) && (
            <div className="absolute right-px top-px flex size-3 items-center justify-center bg-border group-hover:bg-base-dark-border group-data-[state=open]:bg-base-dark-border">
              <div className="size-2 rounded-full bg-link" />
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[608px] w-[420px]">
        <div className="flex justify-between border-b p-3 text-light-secondary">
          <div className="flex items-center">
            <Bell className="size-3.5" />
            <h3 className="p-1">{t("Notifications")}</h3>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="unread-toggle"
              checked={unreadOnly}
              onCheckedChange={setUnreadOnly}
              disabled={!hasNotifications}
              className={`bg-darker-fill data-[state=checked]:bg-link ${!hasNotifications ? "cursor-not-allowed" : ""}`}
            />
            <Label htmlFor="unread-toggle" className="cursor-pointer text-xs text-primary">
              {t("UnreadOnly")}
            </Label>
          </div>
        </div>
        {filteredNotifications?.length === 0 ? (
          <NotificationsEmptyState />
        ) : (
          <div className="max-h-96 overflow-y-auto pr-2">
            {filteredNotifications.map((notification: NotificationWithAvatar) => {
              return (
                <DropdownMenuItem
                  className="flex cursor-default items-start gap-3 border-b !bg-transparent p-3 last:border-b-0"
                  onSelect={(e) => e.preventDefault()}
                >
                  {getNotificationIcon(notification.type)}
                  <div className="max-w-[350px] flex-1">
                    <p className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          onClick={() => handleNotificationClick(notification)}
                          className={`text-xs font-normal text-primary ${!notification.read ? "font-semibold" : ""} cursor-pointer hover:underline`}
                        >
                          {getNotificationTitle(notification.type)}
                        </div>
                        {!notification.read && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="relative ml-[2px] size-2 cursor-pointer rounded-full bg-link transition-all duration-200 hover:ring-2 hover:ring-border hover:ring-offset-[5px]"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    markNotificationAsReadMutation({ notificationId: notification.id.toString() })
                                  }}
                                ></div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t("MarkAsRead")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <UserAvatar
                        username={notification.avatarField.name}
                        avatarURL={notification.avatarField.avatar}
                        className="size-5 text-xxs"
                      />
                    </p>
                    <p className="py-1 text-xxs text-light-secondary">{formatTimeAgo(notification.createdAt)}</p>
                    {"body" in notification.payload && notification.payload.body && (
                      <div className="flex max-w-[328px] text-xs text-light-secondary">
                        <span className="shrink-0">Comment: "</span>
                        <span className="truncate">{notification.payload.body}</span>
                        <span className="shrink-0">"</span>
                      </div>
                    )}
                    <div className="flex">
                      <p
                        className="mr-[10px] inline-block w-[328px] cursor-pointer truncate text-xs text-primary hover:underline"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        {notification.payload.title}
                      </p>
                      <div
                        className="flex size-[22px] cursor-pointer items-center justify-center rounded bg-darker-fill hover:bg-border"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <ArrowUpRight className="size-4 text-primary" />
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              )
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default Notifications
